
import type { Origin } from '@empathybroker/tsclient-stats/dist/models/origins/origin'
import type { OriginsData } from '@empathybroker/tsclient-stats/dist/models/origins/origins-data'
import type { Params } from '../../components/insights/abstract.component'
import type { Config, Data } from '../../components/insights/origins/origins.component'

import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { defineComponent } from 'vue'
import { mapActions } from 'vuex'
import InsightsNoResults from '../../components/insights/insights-no-results.component.vue'
import OriginsLanding from '../../components/insights/origins/origins-landing.component.vue'
import OriginsStep from '../../components/insights/origins/origins-step.component.vue'
import {
  OriginsComponent as Component,
  OriginsStepEnum,
} from '../../components/insights/origins/origins.component'
import IntersectElement from '../../components/intersect-element.component.vue'

import { useAppLoading } from '../../composables/use-app-loading'

import { DefaultOriginIdEnum, PredictiveOriginIdEnum, RefinementOriginIdEnum } from '../../enums'

import { InsightsMixin } from '../../mixins/insights.mixin'

import { APP_CONSTS } from '../../store/app/app.consts'

export default defineComponent({
  name: 'Origins',
  components: {
    OriginsLanding,
    OriginsStep,
    InsightsNoResults,
    IntersectElement,
  },
  mixins: [InsightsMixin],
  setup() {
    const { loading } = useAppLoading()
    return { loading }
  },
  data() {
    return {
      currentStep: undefined,
      stepChangeEnabled: true,
      component: undefined,
      renderFullComponent: false,
      componentTimeout: undefined,
      messages: this.$epb.messages.insights.origins,
    }
  },
  computed: {
    visibleSteps(): OriginsStepEnum[] {
      return (Object.values(OriginsStepEnum) as OriginsStepEnum[]).filter(
        step => !!this.messages.steps[step] && step <= this.currentStep,
      )
    },
    componentParams(): Params<Config, Data> {
      return {
        config: {
          defaultOriginIds: Object.values(DefaultOriginIdEnum),
          predictiveOriginIds: Object.values(PredictiveOriginIdEnum),
          refinementOriginIds: Object.values(RefinementOriginIdEnum),
          locale: this.$epb.locale,
          messages: {
            origins: this.messages.component.origins,
            steps: this.messages.steps,
          },
        },
        data: this.data,
      }
    },
    hasNoResults(): boolean {
      return !this.loading && this.componentParams.data.origins.length === 0
    },
  },
  methods: {
    ...mapActions(APP_CONSTS.moduleName, {
      setFiltersHidden: APP_CONSTS.actions.setFiltersHidden,
    }),
    async fetchData(): Promise<OriginsData> {
      const originsReportResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.ORIGINS,
        params: {},
      })
      return {
        ...originsReportResponse.data,
        origins: originsReportResponse.data.origins.map(({ id, ...rest }: Origin) => ({
          id: id.split(':')[0],
          ...rest,
        })),
      }
    },
    onContentEnter(): void {
      if (!this.component && !this.renderFullComponent) {
        this.component = new Component(this.$refs.component, this.componentParams)
      }
    },
    async onContentAfter(): Promise<void> {
      if (this.renderFullComponent) {
        await this.$nextTick()
        this.component = new Component(this.$refs.component, this.componentParams)

        this.stepChangeEnabled = false
        this.component.setCurrentStep(this.currentStep)
        clearTimeout(this.componentTimeout)
        this.componentTimeout = setTimeout(() => {
          this.onStepChange()
        }, 1000)
      }
    },
    onStepChange(): void {
      if (this.hasNoResults) {
        this.skipAnimation()
      }
      if (!this.currentStep || this.currentStep < OriginsStepEnum.DATA) {
        this.stepChangeEnabled = false
        if (!this.currentStep) {
          this.currentStep = OriginsStepEnum.DEFAULT
        } else {
          this.currentStep++
        }
        if (this.component) {
          this.component.setCurrentStep(this.currentStep)
        }
        if (this.currentStep === OriginsStepEnum.DATA) {
          this.setFiltersHidden(false)
        }
        this.$el.scrollTop = 0
      }
    },
    skipAnimation(): void {
      this.currentStep = OriginsStepEnum.REFINEMENT
      this.renderFullComponent = true
      this.setFiltersHidden(false)
    },
  },
  watch: {
    componentParams: {
      handler(componentParams: Params<Config, Data>): void {
        if (this.component) {
          this.component.update(componentParams)
        }
      },
      deep: true,
    },
  },
  beforeDestroy(): void {
    if (this.component) {
      this.component.destroy()
    }
  },
})
